<script>
/* eslint-disable vue/no-bare-strings-in-template */
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import ReportFilters from './components/ReportFilters.vue';
import AgentPerformanceDashboard from './components/agent/AgentPerformanceDashboard.vue';
import ConversationsListTable from './components/ConversationsListTable.vue';
import ReportDownloadMenu from './components/ReportDownloadMenu.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { getLineChartFilters } from 'dashboard/helper/reportTimeRangeHelper';

const GROUP_BY_OPTIONS = {
  DAY: [{ id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' }],
  WEEK: [
    { id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' },
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
  ],
  MONTH: [
    { id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' },
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
    { id: 3, groupByKey: 'REPORT.GROUPING_OPTIONS.MONTH' },
  ],
  YEAR: [
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
    { id: 3, groupByKey: 'REPORT.GROUPING_OPTIONS.MONTH' },
  ],
};

const REPORT_KEYS = [
  'conversations_count',
  'outgoing_messages_count',
  'avg_first_response_time',
  'avg_resolution_time',
  'resolutions_count',
  'reply_time',
];

export default {
  name: 'AgentPerformanceReports',
  components: {
    AgentPerformanceDashboard,
    ReportFilters,
    ConversationsListTable,
    ReportDownloadMenu,
  },
  props: {
    selectedItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      groupByFilterItems: GROUP_BY_OPTIONS.DAY.map(this.translateOption),
      selectedGroupByFilter: null,
      selectedAgent: this.selectedItem,
      businessHours: false,
      comunicanteCampaign: 'all',
      conversationsPageIndex: 0,
      selectedConversationMetric: '',
      selectedConversationMetricLabel: '',
      isExportingXlsx: false,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      accountSummary: 'getAccountSummary',
      accountReport: 'getAccountReports',
      summaryUIFlags: 'getReportSummaryUIFlags',
    }),
  },
  mounted() {
    this.$store.dispatch('agents/get');
  },
  methods: {
    fetchAllData() {
      this.conversationsPageIndex = 0;
      const payload = this.requestPayload();
      this.$store.dispatch('fetchAccountSummary', payload);
      const chartPayload = getLineChartFilters(payload);
      REPORT_KEYS.forEach(metric => {
        this.$store.dispatch('fetchAccountReport', {
          metric,
          ...chartPayload,
        });
      });
      this.fetchConversationsList(0);
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        type: this.selectedAgent?.id ? 'agent' : 'account',
        id: this.selectedAgent?.id,
        groupBy: this.groupBy.period,
        businessHours: this.businessHours,
        comunicanteCampaign: this.comunicanteCampaign,
      };
    },
    onDateRangeChange({ from, to, groupBy }) {
      this.from = from;
      this.to = to;
      this.groupByFilterItems = this.groupOptionsFor(groupBy);
      this.selectedGroupByFilter = this.groupByFilterItems.find(
        item => item.id === this.groupBy.id
      );

      if (!this.selectedGroupByFilter) {
        this.selectedGroupByFilter = this.groupByFilterItems[0];
        this.groupBy = GROUP_BY_FILTER[this.selectedGroupByFilter.id];
      }
      this.fetchAllData();
    },
    onAgentChange(agent) {
      if ((this.selectedAgent?.id ?? null) === (agent?.id ?? null)) return;

      this.selectedAgent = agent;
      this.conversationsPageIndex = 0;
      this.fetchAllData();
    },
    onGroupByChange(option) {
      this.groupBy = GROUP_BY_FILTER[option.id];
      this.fetchAllData();
      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterType: 'groupBy',
        filterValue: this.groupBy.period,
        reportType: 'agent',
      });
    },
    onBusinessHoursToggle(value) {
      this.businessHours = value;
      this.fetchAllData();
    },
    onComunicanteCampaignChange(selectedCampaign) {
      this.comunicanteCampaign = selectedCampaign.id;
      this.fetchAllData();
    },
    groupOptionsFor(groupBy) {
      switch (groupBy) {
        case GROUP_BY_FILTER[2].period:
          return GROUP_BY_OPTIONS.WEEK.map(this.translateOption);
        case GROUP_BY_FILTER[3].period:
          return GROUP_BY_OPTIONS.MONTH.map(this.translateOption);
        case GROUP_BY_FILTER[4].period:
          return GROUP_BY_OPTIONS.YEAR.map(this.translateOption);
        default:
          return GROUP_BY_OPTIONS.DAY.map(this.translateOption);
      }
    },
    translateOption(option) {
      return { id: option.id, groupBy: this.$t(option.groupByKey) };
    },
    downloadReports() {
      if (!this.from || !this.to) return;

      try {
        this.$store.dispatch('downloadAgentReports', {
          from: this.from,
          to: this.to,
          businessHours: this.businessHours,
          comunicanteCampaign: this.comunicanteCampaign,
          fileName: generateFileName({
            type: 'agent',
            to: this.to,
            businessHours: this.businessHours,
          }),
        });
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
    fetchConversationsList(page = 0) {
      if (!this.from || !this.to) return;

      this.$store.dispatch('fetchConversationsList', {
        metric: this.selectedConversationMetric || 'conversations_count',
        ...this.requestPayload(),
        page: page + 1,
      });
    },
    onConversationsPageChange(pageIndex) {
      this.conversationsPageIndex = pageIndex;
      this.fetchConversationsList(pageIndex);
    },
    onConversationMetricSelect(metric) {
      const metricKey = metric?.key || '';
      const shouldClear = metricKey === this.selectedConversationMetric;
      this.selectedConversationMetric = shouldClear ? '' : metricKey;
      this.selectedConversationMetricLabel = shouldClear ? '' : metric.label;
      this.conversationsPageIndex = 0;
      this.fetchConversationsList(0);
    },
    async downloadConversationsXlsx() {
      if (!this.from || !this.to || this.isExportingXlsx) return;

      this.isExportingXlsx = true;
      try {
        await this.$store.dispatch('downloadConversationsListXlsx', {
          metric: this.selectedConversationMetric || 'conversations_count',
          ...this.requestPayload(),
          fileName: 'conversas-por-agente.xlsx',
        });
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      } finally {
        this.isExportingXlsx = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <div class="flex items-start justify-between gap-4 mb-4">
      <ReportFilters
        class="flex-1"
        type="agent"
        :filter-items-list="agents"
        :group-by-filter-items-list="groupByFilterItems"
        :selected-group-by-filter="selectedGroupByFilter"
        :selected-filter="selectedAgent"
        allow-all
        @date-range-change="onDateRangeChange"
        @filter-change="onAgentChange"
        @group-by-filter-change="onGroupByChange"
        @business-hours-toggle="onBusinessHoursToggle"
        @comunicante-campaign-filter-selection="onComunicanteCampaignChange"
      />
      <ReportDownloadMenu
        class="flex-shrink-0 mt-7"
        :disabled="!from || !to"
        :loading="isExportingXlsx"
        :xlsx-handler="downloadConversationsXlsx"
      />
    </div>

    <!-- prettier-ignore -->
    <div>
    <AgentPerformanceDashboard
      v-if="from && to"
      :account-summary="accountSummary"
      :account-report="accountReport"
      :is-summary-loading="summaryUIFlags.isFetchingAccountSummary"
      :selected-metric="selectedConversationMetric"
      @metric-select="onConversationMetricSelect"
    />
    <section v-if="from && to" class="mt-8">
      <div class="mb-4">
        <div>
          <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Conversas por agente
            <span v-if="selectedConversationMetricLabel">
              · {{ selectedConversationMetricLabel }}
            </span>
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            Lista de conversas baseada nos filtros selecionados.
          </p>
        </div>
      </div>
      <ConversationsListTable
        :page-index="conversationsPageIndex"
        @page-change="onConversationsPageChange"
      />
    </section>
    </div>
  </div>
</template>
